/**
 * Module summary:
 * This page wraps the live assessment form. It resolves the active assessment
 * record, supplies saved answers back into the form, and handles save/submit
 * actions that update the stored record and route to the final report.
 */

import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { QuestionPhotoMap } from '../../types/assessment';
import { AssessmentForm } from '../organisms/AssessmentForm';
import AppShell from '../layout/AppShell';
import { completeAssessment, getActiveAssessmentId, getAssessmentById, saveAssessmentProgress } from '../../services/data';
import { ACTIVE_ASSESSMENT_ID } from '../../services/data/assessmentCatalog';
import './AssessmentWorkspace.css';

export const AssessmentWorkspace: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Prefer the explicit query-string id, then the remembered active id, and finally a local fallback.
  const recordId = params.get('id') || getActiveAssessmentId() || 'AS-LOCAL';
  const record = getAssessmentById(recordId);
  const assessmentId = record?.assessmentId || ACTIVE_ASSESSMENT_ID;
  const buildingId = record?.buildingId || 'building-001';
  const savedResponses = record?.responses || {};
  const savedPhotos = record?.photos || {};

  const handleSubmit = async (
    responses: Record<string, any>,
    photos: QuestionPhotoMap
  ) => {
    // Finalize the assessment record first so the report page opens against the latest saved data.
    await completeAssessment(recordId, responses, photos);
    navigate(`/report/${recordId}`);
  };

  const handleSave = async (responses: Record<string, any>, photos: QuestionPhotoMap) => {
    const ok = await saveAssessmentProgress(recordId, responses, photos);
    if (!ok) {
      // Throw when the record no longer exists so the form can surface a real save failure.
      throw new Error('Unable to save progress for this assessment.');
    }
  };

  return (
    <AppShell title="Assessment Workspace">
      <div className="assessment-workspace-page">
        <div className="assessment-workspace-page__intro">
          <p className="assessment-workspace-page__eyebrow">Assessment Workspace</p>
          <h1 className="assessment-workspace-page__title">{record?.name || 'Security Assessment'}</h1>
          <p className="assessment-workspace-page__subtitle">
            Complete the walkthrough, save progress as you go, and submit when the assessment is ready.
          </p>
        </div>

        <div className="assessment-workspace-page__body">
          <AssessmentForm
            assessmentId={assessmentId}
            buildingId={buildingId}
          onSubmit={handleSubmit}
          onSave={handleSave}
          initialData={savedResponses}
          initialPhotos={savedPhotos}
        />
      </div>
      </div>
    </AppShell>
  );
};

export default AssessmentWorkspace;
